/**
 * Free/busy for the scheduling assistant. Two services each report what they
 * own — the Mail BFF each person's Mail calendar (plus their working hours and
 * time zone), the Connect BFF their Connect meetings — and this merges them.
 * Both enforce the same rule server-side: only people in the caller's tenant,
 * busy times only, titles only where the viewer is entitled to them.
 *
 * (This file is kept identical in Mail web and Connect web.)
 */

const mailBase = import.meta.env.VITE_MAIL_BFF_BASE_URL ?? "http://localhost:5102";
const connectBase = import.meta.env.VITE_CONNECT_BFF_BASE_URL ?? "http://localhost:5104";

export type ShowAs = "busy" | "tentative";

export interface BusyBlock {
    start: Date;
    end: Date;
    showAs: ShowAs;
    /** Null unless the viewer may see it (a share, or a meeting they're in). */
    subject: string | null;
    source: "mail" | "connect";
    allDay: boolean;
}

export interface Availability {
    email: string;
    displayName: string | null;
    /** False for people outside your organisation — no data then. */
    available: boolean;
    timeZone: string | null;
    /** ISO weekdays, 1 = Monday. */
    workDays: number[] | null;
    workStart: string | null;
    workEnd: string | null;
    busy: BusyBlock[];
}

interface MailPerson {
    email: string;
    displayName?: string | null;
    available: boolean;
    timeZone?: string | null;
    workDays?: number[] | null;
    workStart?: string | null;
    workEnd?: string | null;
    busy: Array<{ start: string; end: string; isAllDay: boolean; showAs: string; subject?: string | null }>;
}

interface ConnectPerson {
    email: string;
    available: boolean;
    busy: Array<{ start: string; end: string; showAs: string; subject?: string | null }>;
}

async function post<T>(base: string, path: string, body: unknown, token: string | undefined): Promise<T> {
    const headers = new Headers({ "Content-Type": "application/json", Accept: "application/json" });
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(new URL(path, base).toString(), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        credentials: "omit"
    });
    if (!res.ok) throw new Error(`${path} → ${res.status}`);
    return (await res.json()) as T;
}

const showAs = (v: string): ShowAs => (v === "tentative" ? "tentative" : "busy");

/** Everyone's availability in [from, to), keyed by lower-cased email. */
export async function fetchAvailability(
    emails: string[],
    from: Date,
    to: Date,
    token: string | undefined
): Promise<Map<string, Availability>> {
    const body = { emails, from: from.toISOString(), to: to.toISOString() };
    const [mail, connect] = await Promise.allSettled([
        post<MailPerson[]>(mailBase, "/api/calendar/free-busy", body, token),
        post<ConnectPerson[]>(connectBase, "/api/calendar/free-busy", body, token)
    ]);
    if (mail.status === "rejected" && connect.status === "rejected") {
        throw new Error("Availability couldn't be loaded");
    }

    const map = new Map<string, Availability>();
    const entry = (email: string): Availability => {
        const key = email.toLowerCase();
        let a = map.get(key);
        if (!a) {
            a = { email: key, displayName: null, available: false, timeZone: null, workDays: null, workStart: null, workEnd: null, busy: [] };
            map.set(key, a);
        }
        return a;
    };

    if (mail.status === "fulfilled") {
        for (const p of mail.value) {
            const a = entry(p.email);
            a.available ||= p.available;
            a.displayName = p.displayName ?? a.displayName;
            a.timeZone = p.timeZone ?? a.timeZone;
            a.workDays = p.workDays ?? a.workDays;
            a.workStart = p.workStart ?? a.workStart;
            a.workEnd = p.workEnd ?? a.workEnd;
            for (const b of p.busy) {
                a.busy.push({
                    start: new Date(b.start),
                    end: new Date(b.end),
                    showAs: showAs(b.showAs),
                    subject: b.subject ?? null,
                    source: "mail",
                    allDay: b.isAllDay
                });
            }
        }
    }
    if (connect.status === "fulfilled") {
        for (const p of connect.value) {
            const a = entry(p.email);
            a.available ||= p.available;
            for (const b of p.busy) {
                a.busy.push({
                    start: new Date(b.start),
                    end: new Date(b.end),
                    showAs: showAs(b.showAs),
                    subject: b.subject ?? null,
                    source: "connect",
                    allDay: false
                });
            }
        }
    }
    return map;
}
